<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ArrowLeft, Eye, Film, ListVideo, Pencil, RefreshCw, RotateCcw, Search } from '@lucide/vue'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Section,
  Select,
  Spinner,
  Stat,
  Textarea,
  Toolbar,
  useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import {
  listGenerationRecordGroups,
  listGenerationRecordScenarios,
  listGenerationRecords,
  updateGenerationRecordVideoCopy,
} from '../api/generation-records.js'
import { formatDate, formatDuration, formatOptional, formatSize, statusVariant } from './generation-records-format.js'
import { formatInt } from './llm/llm-format.js'
import MediaPreviewModal from './llm/call/MediaPreviewModal.vue'

const PAGE_SIZES = [20, 50, 100]
const STATUS_KEYS = ['queued', 'running', 'ready', 'failed']
/** `variant` isn't a closed enum — it grows as new production pipelines ship. Unlisted values fall back to the raw code. */
const VARIANT_LABEL_KEYS = {
  final: 'endingVideo',
  gameplay_replay_v1: 'gameplayReplay',
  gameplay_turn_v1: 'gameplayTurn',
}

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()

const filters = reactive({ q: '', status: '', scenario_id: '' })
const scenarioFacets = ref([])
const videoTotal = ref(0)
const videoPlays = ref(0)
const stats = ref({ queued: 0, running: 0, ready: 0, failed: 0 })

/**
 * Two-level navigation, not a flat filtered list: `groups` is a real `GROUP BY scenario+ending`
 * over every matching row, so its counts are exact regardless of pagination. Picking a group locks
 * `activeGroup` and switches to `detail`, which is the only place `videoRecords`/pagination apply —
 * grouping and paging never try to share one query again.
 */
const view = ref('groups')
const groups = ref([])
const groupsLoading = ref(false)
const groupsError = ref('')
const activeGroup = ref(null)

const page = ref(1)
const pageSize = ref(PAGE_SIZES[0])
const loading = ref(false)
const error = ref('')
const videoRecords = ref([])
const detailTotal = ref(0)

const editOpen = ref(false)
const saving = ref(false)
const editingVideoId = ref('')
const copyForm = reactive({ title: '', summary: '' })

const previewOpen = ref(false)
const previewUrl = ref('')
const previewTitle = ref('')

const statusOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.generationRecords.filters.allStatuses') },
  ...STATUS_KEYS.map(status => ({ value: status, label: t(`visualGameAdmin.generationRecords.status.${status}`) })),
])

const scenarioOptions = computed(() => [
  { value: '', label: t('visualGameAdmin.generationRecords.filters.allScenarios') },
  ...scenarioFacets.value.map(scenario => ({ value: scenario.scenario_id, label: scenario.scenario_title })),
])

const groupTiles = computed(() => groups.value.map(group => ({
  ...group,
  key: `${group.scenario_id}::${group.ending_id}`,
  storyLine: group.scenario_title ?? group.scenario_id,
  endingLine: group.ending_title ?? group.ending_id,
  countLabel: t('visualGameAdmin.generationRecords.group.count', { count: group.total }),
  updatedLabel: formatDate(group.latest_updated_at),
})))

const pageCount = computed(() => Math.max(1, Math.ceil(detailTotal.value / pageSize.value)))
const pageSummary = computed(() => t('visualGameAdmin.generationRecords.pagination.summary', {
  total: detailTotal.value.toLocaleString(),
  page: page.value,
  pages: pageCount.value,
}))

const cards = computed(() => videoRecords.value.map(row => ({
  ...row,
  typeLine: [variantLabel(row.variant), versionLabel(row.scenario_version_id), row.provider].filter(Boolean).join(' · '),
  userLine: row.user_email ?? row.user_id,
  pipelineSteps: pipelineSteps(row),
  pipelineCaption: pipelineCaption(row),
  engagementPlays: t('visualGameAdmin.generationRecords.metrics.plays', { count: formatInt(row.play_count) }),
  engagementLikes: t('visualGameAdmin.generationRecords.metrics.likesAndFavorites', {
    likes: formatInt(row.like_count),
    favorites: formatInt(row.favorite_count),
  }),
  copyPrimary: row.status === 'failed' ? row.error_message : row.video_title,
  copySecondary: row.status === 'failed' ? null : row.video_summary,
  metricsPrimary: formatDuration(row.duration_ms),
  metricsSecondary: formatSize(row.size),
  updated_at: formatDate(row.updated_at),
})))

function variantLabel(variant) {
  const key = VARIANT_LABEL_KEYS[variant]
  return key ? t(`visualGameAdmin.generationRecords.variant.${key}`) : variant
}

/** Scenario version ids are `<pack_id>:v<n>`, e.g. `painted_skin_room:v52` — the trailing segment is already the human version tag. */
function versionLabel(scenarioVersionId) {
  return scenarioVersionId ? scenarioVersionId.slice(scenarioVersionId.lastIndexOf(':') + 1) : null
}

/**
 * There's no scheduler table behind this — every run video only ever moves through these four
 * stages: the run itself finished, the video was requested (this row's own existence), the
 * adaptation agent wrote a screenplay (`vg_run_turn_video_screenplays` row, keyed by run_id), and
 * production filled `vg_run_videos` back in. `run`/`requested` are always done by the time a video
 * shows up here at all — they're drawn to keep the four-stage relationship visible, not because
 * they're ever pending.
 */
function pipelineSteps(row) {
  const screenplayDone = row.status === 'ready' || row.screenplay_ready === true
  const screenplayFailed = row.status === 'failed' && row.screenplay_ready === false
  const productionDone = row.status === 'ready'
  const productionFailed = row.status === 'failed' && row.screenplay_ready === true
  const productionActive = row.status === 'running'
  return [
    { key: 'run', state: 'done' },
    { key: 'requested', state: 'done' },
    { key: 'screenplay', state: screenplayFailed ? 'failed' : screenplayDone ? 'done' : 'pending' },
    { key: 'production', state: productionFailed ? 'failed' : productionDone ? 'done' : productionActive ? 'active' : 'pending' },
  ]
}

function pipelineCaption(row) {
  if (row.status === 'ready') return t('visualGameAdmin.generationRecords.pipeline.ready')
  if (row.status === 'failed') {
    return t(`visualGameAdmin.generationRecords.pipeline.${row.screenplay_ready ? 'failedProduction' : 'failedScreenplay'}`)
  }
  if (row.status === 'running' || row.screenplay_ready) return t('visualGameAdmin.generationRecords.pipeline.producing')
  return t('visualGameAdmin.generationRecords.pipeline.awaitingScreenplay')
}

/**
 * The drilled-into group (and the filters around it) lives in the URL query, not just component
 * state — otherwise there is nothing to bookmark, share, or reach with the browser's back button.
 */
onMounted(async () => {
  if (typeof route.query.q === 'string') filters.q = route.query.q
  if (typeof route.query.status === 'string') filters.status = route.query.status
  if (typeof route.query.scenario_id === 'string') filters.scenario_id = route.query.scenario_id
  await loadScenarioFacets()
  if (typeof route.query.ending_id === 'string' && filters.scenario_id) {
    activeGroup.value = {
      scenario_id: filters.scenario_id,
      ending_id: route.query.ending_id,
      storyLine: filters.scenario_id,
      endingLine: route.query.ending_id,
    }
    view.value = 'detail'
    await Promise.all([loadStats(), loadDetail()])
    return
  }
  await Promise.all([loadStats(), loadGroups()])
})

/** Query shared by the groups overview and the top stats bar — everything except pagination and the drilled-into group. */
function groupFilters() {
  const query = {}
  if (filters.q.trim()) query.q = filters.q.trim()
  if (filters.status) query.status = filters.status
  if (filters.scenario_id) query.scenario_id = filters.scenario_id
  return query
}

/** Pushes the current filters + drill-down onto the URL so this exact view has a shareable link. */
function syncUrl() {
  const query = { ...groupFilters() }
  if (activeGroup.value) query.ending_id = activeGroup.value.ending_id
  router.push({ name: 'generation-records', query })
}

/** Independent of `view`/`page` on purpose — pagination or drilling into a group must never move these numbers. */
async function loadStats() {
  const response = await listGenerationRecords({ ...groupFilters(), limit: 1, offset: 0 })
  videoTotal.value = response.video_records.total
  videoPlays.value = response.video_records.plays
  stats.value = response.video_records.stats
}

async function loadGroups() {
  groupsLoading.value = true
  groupsError.value = ''
  try {
    const response = await listGenerationRecordGroups(groupFilters())
    groups.value = response.groups
  } catch (requestError) {
    groupsError.value = requestError.message
  } finally {
    groupsLoading.value = false
  }
}

async function loadDetail() {
  loading.value = true
  error.value = ''
  try {
    const query = {
      ...groupFilters(),
      scenario_id: activeGroup.value.scenario_id,
      ending_id: activeGroup.value.ending_id,
      limit: pageSize.value,
      offset: (page.value - 1) * pageSize.value,
    }
    const response = await listGenerationRecords(query)
    videoRecords.value = response.video_records.items
    detailTotal.value = response.video_records.total
    // A group reached via a deep link only knows the raw ids until the first row confirms the
    // human titles — refine every load so a stale title never lingers after filters change.
    const first = videoRecords.value[0]
    if (activeGroup.value && first) {
      activeGroup.value.storyLine = first.scenario_title ?? activeGroup.value.scenario_id
      activeGroup.value.endingLine = first.ending_title ?? activeGroup.value.ending_id
    }
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

async function loadScenarioFacets() {
  const response = await listGenerationRecordScenarios()
  scenarioFacets.value = response.scenarios
}

function openGroup(group) {
  activeGroup.value = group
  filters.scenario_id = group.scenario_id
  page.value = 1
  view.value = 'detail'
  syncUrl()
  return loadDetail()
}

function backToGroups() {
  view.value = 'groups'
  activeGroup.value = null
  syncUrl()
}

/** The toolbar always searches at the groups level — changing scope while drilled into one group would leave it inconsistent. */
function applyFilters() {
  view.value = 'groups'
  activeGroup.value = null
  syncUrl()
  return Promise.all([loadStats(), loadGroups()])
}

function resetFilters() {
  filters.q = ''
  filters.status = ''
  filters.scenario_id = ''
  return applyFilters()
}

function refresh() {
  return view.value === 'detail' ? Promise.all([loadStats(), loadDetail()]) : Promise.all([loadStats(), loadGroups()])
}

function changePageSize(value) {
  pageSize.value = value
  page.value = 1
  return loadDetail()
}

function nextPage() {
  page.value += 1
  return loadDetail()
}

function prevPage() {
  page.value -= 1
  return loadDetail()
}

function openPreview(row) {
  previewUrl.value = row.public_url
  previewTitle.value = row.video_title || t('visualGameAdmin.generationRecords.preview.title')
  previewOpen.value = true
}

function openProcess(row) {
  return router.push({ name: 'generation-record-process', params: { id: row.id } })
}

function canEditVideoCopy(row) {
  return row.status === 'ready' && row.video_copy_status === 'ready'
}

function openCopyEditor(row) {
  if (!canEditVideoCopy(row)) throw new Error(`Video copy is not editable: ${row.id}`)
  editingVideoId.value = row.id
  copyForm.title = row.video_title
  copyForm.summary = row.video_summary
  editOpen.value = true
}

async function saveVideoCopy() {
  saving.value = true
  try {
    const updated = await updateGenerationRecordVideoCopy(editingVideoId.value, {
      title: copyForm.title.trim(),
      summary: copyForm.summary.trim(),
    })
    const index = videoRecords.value.findIndex(row => row.id === updated.id)
    if (index === -1) throw new Error(`Updated video record is missing from the current list: ${updated.id}`)
    videoRecords.value[index] = updated
    editOpen.value = false
    toast.success(t('visualGameAdmin.generationRecords.edit.saved'))
  } catch (cause) {
    toast.error(cause.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

function statTone(status) {
  switch (status) {
    case 'queued':
    case 'running':
      return 'neutral'
    case 'ready':
      return 'success'
    case 'failed':
      return 'danger'
    default:
      throw new Error(`Unhandled generation record status: ${status}`)
  }
}

</script>

<template>
  <div class="generation-records-page vga-page">
    <PageHeader :title="t('visualGameAdmin.generationRecords.title')">
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loading || groupsLoading" @click="refresh">
          <RefreshCw :size="14" />
          {{ t('visualGameAdmin.generationRecords.actions.refresh') }}
        </Button>
      </template>
    </PageHeader>

    <div class="generation-records-page__stats" data-insp-path="apps/visual-game-admin/src/pages/GenerationRecords.vue#stats">
      <Stat :label="t('visualGameAdmin.generationRecords.stats.total')" :value="videoTotal" />
      <Stat :label="t('visualGameAdmin.generationRecords.stats.plays')" :value="formatInt(videoPlays)" />
      <Stat
        v-for="status in STATUS_KEYS"
        :key="status"
        :label="t(`visualGameAdmin.generationRecords.status.${status}`)"
        :value="stats[status]"
        :tone="statTone(status)"
      />
    </div>

    <Section data-insp-path="apps/visual-game-admin/src/pages/GenerationRecords.vue#filters">
      <Toolbar>
        <div class="drama-toolbar-field drama-toolbar-field--wide">
          <label>{{ t('visualGameAdmin.generationRecords.filters.q') }}</label>
          <Input
            v-model="filters.q"
            type="search"
            size="sm"
            :placeholder="t('visualGameAdmin.generationRecords.filters.qPlaceholder')"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.generationRecords.filters.status') }}</label>
          <Select v-model="filters.status" :options="statusOptions" size="sm" />
        </div>
        <div class="drama-toolbar-field">
          <label>{{ t('visualGameAdmin.generationRecords.filters.scenario') }}</label>
          <Select v-model="filters.scenario_id" :options="scenarioOptions" size="sm" />
        </div>
        <template #actions>
          <Button size="sm" :loading="groupsLoading" @click="applyFilters">
            <Search :size="14" />
            {{ t('visualGameAdmin.generationRecords.filters.search') }}
          </Button>
          <Button variant="ghost" size="sm" :disabled="groupsLoading" @click="resetFilters">
            <RotateCcw :size="14" />
            {{ t('visualGameAdmin.common.reset') }}
          </Button>
        </template>
      </Toolbar>
    </Section>

    <template v-if="view === 'groups'">
      <div v-if="groupsLoading" class="vga-state">
        <Spinner />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
      <div v-else-if="groupsError" class="vga-state">
        <p class="vga-error">{{ groupsError }}</p>
        <Button variant="secondary" @click="loadGroups">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState
        v-else-if="groupTiles.length === 0"
        :title="t('visualGameAdmin.generationRecords.empty.groups.title')"
        :description="t('visualGameAdmin.generationRecords.empty.groups.description')"
      />
      <div v-else class="generation-records-page__groups-grid" data-insp-path="apps/visual-game-admin/src/pages/GenerationRecords.vue#groups">
        <Card
          v-for="group in groupTiles"
          :key="group.key"
          interactive
          class="generation-records-page__group-tile"
          @click="openGroup(group)"
        >
          <h3 class="generation-records-page__group-tile-title">{{ group.storyLine }} · {{ group.endingLine }}</h3>
          <div class="generation-records-page__group-tile-meta">
            <Badge size="sm">{{ group.countLabel }}</Badge>
            <span>{{ group.updatedLabel }}</span>
          </div>
        </Card>
      </div>
    </template>

    <template v-else>
      <div class="generation-records-page__section-title">
        <Button variant="ghost" size="sm" @click="backToGroups">
          <ArrowLeft :size="14" />
          {{ t('visualGameAdmin.generationRecords.actions.backToGroups') }}
        </Button>
        <Film :size="18" />
        <h2>{{ activeGroup.storyLine }} · {{ activeGroup.endingLine }}</h2>
      </div>

      <div v-if="loading" class="vga-state">
        <Spinner />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>
      <div v-else-if="error" class="vga-state">
        <p class="vga-error">{{ error }}</p>
        <Button variant="secondary" @click="loadDetail">{{ t('visualGameAdmin.common.retry') }}</Button>
      </div>
      <EmptyState
        v-else-if="videoRecords.length === 0"
        :title="t('visualGameAdmin.generationRecords.empty.videos.title')"
        :description="t('visualGameAdmin.generationRecords.empty.videos.description')"
      />
      <div v-else class="generation-records-page__grid" data-insp-path="apps/visual-game-admin/src/pages/GenerationRecords.vue#videos">
        <Card
          v-for="row in cards"
          :key="row.id"
          :padded="false"
          :interactive="!!row.public_url"
          class="generation-records-page__card"
          @click="row.public_url && openPreview(row)"
        >
          <div class="generation-records-page__thumb">
            <video v-if="row.public_url" :src="row.public_url" muted playsinline preload="metadata" />
            <Film v-else :size="28" class="generation-records-page__thumb-icon" />
            <Badge class="generation-records-page__thumb-status" :variant="statusVariant(row.status)" size="sm">
              {{ t(`visualGameAdmin.generationRecords.status.${row.status}`) }}
            </Badge>
            <span v-if="row.public_url" class="generation-records-page__thumb-play">
              <Eye :size="18" />
            </span>
          </div>

          <div class="generation-records-page__card-body">
            <h4 class="generation-records-page__card-title">{{ formatOptional(row.copyPrimary) }}</h4>
            <p class="generation-records-page__card-summary">{{ row.copySecondary }}</p>

            <div class="generation-records-page__pipeline">
              <div class="generation-records-page__pipeline-steps">
                <span
                  v-for="step in row.pipelineSteps"
                  :key="step.key"
                  class="generation-records-page__pipeline-step"
                  :class="`is-${step.state}`"
                  :title="t(`visualGameAdmin.generationRecords.pipelineStep.${step.key}`)"
                />
              </div>
              <span class="generation-records-page__pipeline-caption">{{ row.pipelineCaption }}</span>
            </div>

            <div class="generation-records-page__card-meta">
              <span>{{ row.typeLine }}</span>
              <span>{{ row.userLine }}</span>
              <span>{{ row.engagementPlays }} · {{ row.engagementLikes }}</span>
              <span>{{ row.metricsPrimary }} · {{ row.metricsSecondary }}</span>
            </div>

            <div class="generation-records-page__card-footer">
              <span class="generation-records-page__card-time">{{ row.updated_at }}</span>
              <div class="generation-records-page__card-actions">
                <Button variant="ghost" size="sm" @click.stop="openProcess(row)">
                  <ListVideo :size="14" />
                  {{ t('visualGameAdmin.generationRecords.actions.viewProcess') }}
                </Button>
                <Button v-if="canEditVideoCopy(row)" variant="secondary" size="sm" @click.stop="openCopyEditor(row)">
                  <Pencil :size="14" />
                  {{ t('visualGameAdmin.generationRecords.actions.editCopy') }}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Pagination
        v-if="videoRecords.length > 0"
        :has-next="page < pageCount"
        :has-prev="page > 1"
        :loading="loading"
        :prev-label="t('visualGameAdmin.generationRecords.pagination.prev')"
        :next-label="t('visualGameAdmin.generationRecords.pagination.next')"
        :summary="pageSummary"
        :page-size="pageSize"
        :page-size-options="PAGE_SIZES"
        :page-size-label="t('visualGameAdmin.generationRecords.pagination.pageSize')"
        @prev="prevPage"
        @next="nextPage"
        @update:page-size="changePageSize"
      />
    </template>

    <MediaPreviewModal
      v-model:open="previewOpen"
      capability="video"
      :title="previewTitle"
      :url="previewUrl"
    />

    <Modal
      :open="editOpen"
      :title="t('visualGameAdmin.generationRecords.edit.title')"
      :close-on-backdrop="!saving"
      width="680px"
      @update:open="editOpen = $event"
    >
      <form class="generation-records-page__copy-form" @submit.prevent="saveVideoCopy">
        <FormField :label="t('visualGameAdmin.generationRecords.edit.videoTitle')" required>
          <Input v-model="copyForm.title" :maxlength="120" block required />
        </FormField>
        <FormField :label="t('visualGameAdmin.generationRecords.edit.videoSummary')" required>
          <Textarea v-model="copyForm.summary" :maxlength="2000" :rows="6" block required />
        </FormField>
      </form>
      <template #footer>
        <Button variant="ghost" :disabled="saving" @click="editOpen = false">
          {{ t('visualGameAdmin.common.cancel') }}
        </Button>
        <Button
          variant="primary"
          :loading="saving"
          :disabled="copyForm.title.trim() === '' || copyForm.summary.trim() === ''"
          @click="saveVideoCopy"
        >
          {{ t('visualGameAdmin.common.save') }}
        </Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
.generation-records-page__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--drama-space-md);
}

.generation-records-page__section-title {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md) var(--drama-space-lg);
  border-bottom: 1px solid var(--drama-color-border);
}

.generation-records-page__section-title h2 {
  margin: 0;
  font-size: var(--drama-font-size-md);
}

.generation-records-page__groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--drama-space-md);
}

.generation-records-page__group-tile-title {
  margin: 0 0 var(--drama-space-sm);
  font-size: var(--drama-font-size-md);
}

.generation-records-page__group-tile-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.generation-records-page__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--drama-space-lg);
}

.generation-records-page__card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.generation-records-page__thumb {
  position: relative;
  aspect-ratio: 16 / 9;
  display: grid;
  place-items: center;
  background: var(--drama-color-bg);
  overflow: hidden;
}

.generation-records-page__thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.generation-records-page__thumb-icon {
  color: var(--drama-color-text-muted);
}

.generation-records-page__thumb-status {
  position: absolute;
  top: var(--drama-space-sm);
  left: var(--drama-space-sm);
}

.generation-records-page__thumb-play {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #fff;
  opacity: 0;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.generation-records-page__card:hover .generation-records-page__thumb-play {
  opacity: 1;
  background: rgba(0, 0, 0, 0.35);
}

.generation-records-page__card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-lg);
}

.generation-records-page__card-title {
  display: -webkit-box;
  min-height: 2.6em;
  margin: 0;
  overflow: hidden;
  font-size: var(--drama-font-size-md);
  font-weight: var(--drama-font-weight-medium);
  line-height: 1.3;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.generation-records-page__card-summary {
  display: -webkit-box;
  min-height: 2.8em;
  margin: 0;
  overflow: hidden;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  line-height: 1.4;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.generation-records-page__pipeline {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}

.generation-records-page__pipeline-steps {
  display: flex;
  flex-shrink: 0;
  align-items: center;
}

.generation-records-page__pipeline-step {
  width: 8px;
  height: 8px;
  border: 1.5px solid var(--drama-color-border);
  border-radius: 50%;
  background: var(--drama-color-surface-alt);
}

.generation-records-page__pipeline-step + .generation-records-page__pipeline-step {
  position: relative;
  margin-left: 4px;
}

.generation-records-page__pipeline-step + .generation-records-page__pipeline-step::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 100%;
  width: 4px;
  height: 1.5px;
  background: var(--drama-color-border);
  transform: translateY(-50%);
}

.generation-records-page__pipeline-step.is-done {
  background: var(--drama-color-success);
  border-color: var(--drama-color-success);
}

.generation-records-page__pipeline-step.is-active {
  background: var(--drama-color-primary);
  border-color: var(--drama-color-primary);
  animation: generation-records-page-pulse 1.2s ease-in-out infinite;
}

.generation-records-page__pipeline-step.is-failed {
  background: var(--drama-color-danger);
  border-color: var(--drama-color-danger);
}

.generation-records-page__pipeline-caption {
  overflow: hidden;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes generation-records-page-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

.generation-records-page__card-meta {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-2xs, 2px);
  padding-top: var(--drama-space-sm);
  border-top: 1px solid var(--drama-color-border);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.generation-records-page__card-meta span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.generation-records-page__card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
  margin-top: auto;
  padding-top: var(--drama-space-sm);
  border-top: 1px solid var(--drama-color-border);
}

.generation-records-page__card-actions {
  display: flex;
  gap: var(--drama-space-xs);
}

.generation-records-page__card-time {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.generation-records-page__copy-form {
  display: grid;
  gap: var(--drama-space-lg);
  width: 100%;
}
</style>
